/**
 * Dev tuning overlay — lets you tweak game feel constants in real-time.
 * Toggle with backtick (`) key.
 */

export interface TunableParam {
  label: string;
  key: string;
  min: number;
  max: number;
  step: number;
  get: () => number;
  set: (v: number) => void;
}

export class TuningPanel {
  private container: HTMLElement;
  private visible = false;
  private params: TunableParam[] = [];
  private sliders = new Map<string, HTMLInputElement>();
  private values = new Map<string, HTMLSpanElement>();
  private onKeyDown: (e: KeyboardEvent) => void;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'tuning-panel';
    this.container.style.cssText = `
      position: fixed; top: 0; left: 0; bottom: 0;
      width: 300px; max-width: 80vw;
      background: rgba(10, 10, 26, 0.92);
      border-right: 1px solid #00ffff44;
      padding: 12px;
      overflow-y: auto;
      z-index: 1000;
      font-family: 'Courier New', monospace;
      font-size: 12px;
      color: #00ffff;
      display: none;
      backdrop-filter: blur(4px);
    `;

    const title = document.createElement('div');
    title.style.cssText = `
      font-size: 14px; font-weight: bold; margin-bottom: 12px;
      padding-bottom: 8px; border-bottom: 1px solid #00ffff44;
      display: flex; justify-content: space-between; align-items: center;
    `;
    title.innerHTML = `TUNING PANEL <span style="opacity:0.5;font-size:11px">\` to toggle</span>`;
    this.container.appendChild(title);

    document.body.appendChild(this.container);

    this.onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Backquote') {
        e.preventDefault();
        this.toggle();
      }
    };
    window.addEventListener('keydown', this.onKeyDown);
  }

  register(params: TunableParam[]): void {
    this.params = params;
    this.rebuild();
  }

  private rebuild(): void {
    // Remove old param rows (keep title)
    while (this.container.children.length > 1) {
      this.container.removeChild(this.container.lastChild!);
    }
    this.sliders.clear();
    this.values.clear();

    let currentGroup = '';

    for (const p of this.params) {
      // Group header from key prefix (e.g. "player.gravity" → "PLAYER")
      const group = p.key.split('.')[0].toUpperCase();
      if (group !== currentGroup) {
        currentGroup = group;
        const header = document.createElement('div');
        header.style.cssText = `
          margin-top: 12px; margin-bottom: 6px; font-size: 11px;
          color: #ff00ff; font-weight: bold; letter-spacing: 0.1em;
        `;
        header.textContent = `── ${group} ──`;
        this.container.appendChild(header);
      }

      const row = document.createElement('div');
      row.style.cssText = 'margin-bottom: 8px;';

      const labelRow = document.createElement('div');
      labelRow.style.cssText = 'display: flex; justify-content: space-between; margin-bottom: 2px;';

      const label = document.createElement('span');
      label.textContent = p.label;
      label.style.opacity = '0.8';

      const valueSpan = document.createElement('span');
      valueSpan.textContent = String(p.get());
      valueSpan.style.color = '#ffff00';
      this.values.set(p.key, valueSpan);

      labelRow.appendChild(label);
      labelRow.appendChild(valueSpan);

      const slider = document.createElement('input');
      slider.type = 'range';
      slider.min = String(p.min);
      slider.max = String(p.max);
      slider.step = String(p.step);
      slider.value = String(p.get());
      slider.style.cssText = `
        width: 100%; height: 4px; -webkit-appearance: none; appearance: none;
        background: #00ffff33; outline: none; border-radius: 2px; cursor: pointer;
      `;
      // Thumb styling via class
      slider.classList.add('tuning-slider');

      slider.addEventListener('input', () => {
        const val = parseFloat(slider.value);
        p.set(val);
        valueSpan.textContent = p.step >= 1 ? String(Math.round(val)) : val.toFixed(2);
      });

      this.sliders.set(p.key, slider);

      row.appendChild(labelRow);
      row.appendChild(slider);
      this.container.appendChild(row);
    }

    // Reset button
    const resetBtn = document.createElement('button');
    resetBtn.textContent = 'Reset All';
    resetBtn.style.cssText = `
      margin-top: 16px; padding: 6px 16px; width: 100%;
      background: transparent; border: 1px solid #ff006688;
      color: #ff0066; font-family: inherit; font-size: 12px;
      cursor: pointer; border-radius: 2px;
    `;
    resetBtn.addEventListener('click', () => this.resetAll());
    this.container.appendChild(resetBtn);

    // Add slider thumb styles
    if (!document.getElementById('tuning-slider-style')) {
      const style = document.createElement('style');
      style.id = 'tuning-slider-style';
      style.textContent = `
        .tuning-slider::-webkit-slider-thumb {
          -webkit-appearance: none; appearance: none;
          width: 12px; height: 12px; border-radius: 50%;
          background: #00ffff; cursor: pointer;
        }
        .tuning-slider::-moz-range-thumb {
          width: 12px; height: 12px; border-radius: 50%;
          background: #00ffff; cursor: pointer; border: none;
        }
      `;
      document.head.appendChild(style);
    }
  }

  private defaults = new Map<string, number>();

  /** Snapshot current values as defaults (call after register) */
  captureDefaults(): void {
    for (const p of this.params) {
      this.defaults.set(p.key, p.get());
    }
  }

  private resetAll(): void {
    for (const p of this.params) {
      const def = this.defaults.get(p.key);
      if (def !== undefined) {
        p.set(def);
        const slider = this.sliders.get(p.key);
        const valueSpan = this.values.get(p.key);
        if (slider) slider.value = String(def);
        if (valueSpan) valueSpan.textContent = p.step >= 1 ? String(Math.round(def)) : def.toFixed(2);
      }
    }
  }

  toggle(): void {
    this.visible = !this.visible;
    this.container.style.display = this.visible ? 'block' : 'none';
  }

  hide(): void {
    this.visible = false;
    this.container.style.display = 'none';
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    this.container.remove();
  }
}
